import styled, { CSSObject, createGlobalStyle, css } from 'styled-components';
import uniq from 'lodash/uniq';
import { fonts } from '~/src/helpers/font';
import { iconsBreakpoints } from '~/src/components/Layout';

export const GlobalStyle = createGlobalStyle`
    .card-preview {
        font-family: 'AeroportRegular', Helvetica, sans-serif;
        font-weight: 400;
        font-style: normal;
        text-rendering: optimizeLegibility;
    }
    
    p {
        margin-bottom: 0.5rem;
    }
    
    strong {
        font: ${fonts.Medium};
    }
    
    em {
        font-style: italic;
    }
    
    u {
        text-decoration: underline;
    }
`;

export interface CSSPropWithBreakpoints extends CSSObject {
    desktop?: CSSObject;
    wDesktop?: CSSObject;
    sDesktop?: CSSObject;
    tablet?: CSSObject;
    mobile?: CSSObject;
}

export interface CSSPropWithBreakpointsGeneric <T>{
    desktop?: T;
    sDesktop?: T;
    tablet?: T;
    mobile?: T;
}

export const breakPoints = {
    desktop: 'auto',
    wDesktop: '1649px',
    sDesktop: '1365px',
    tablet: '1023px',
    mobile: '767px',
};

export const styleWithBreakPoints = (p: any, propName: string, tag = '') => {
    if (p[propName]?.meta?.isAdmin) {
        let result = p[propName]?.desktop.filter(item => item.indexOf(tag) !== -1);
        for (const breakpoint of iconsBreakpoints) {
            const props = p[propName][breakpoint.name] ?? [];
            result = [...result, ...props.filter(item => item.indexOf(tag) !== -1)];
            if (breakpoint.name === p[propName]?.meta.currentBreakpoint) break;
        }

        return css`${uniq(result).join('')}`;
    }

    return css`
        ${p[propName]?.desktop?.filter(item => item.indexOf(tag) !== -1).join('') ?? ''};
    
        @media (max-width: ${breakPoints.wDesktop}) {
            ${p[propName]?.wDesktop?.filter(item => item.indexOf(tag) !== -1).join('') ?? ''};
        }
    
        @media (max-width: ${breakPoints.sDesktop}) {
            ${p[propName]?.sDesktop?.filter(item => item.indexOf(tag) !== -1).join('') ?? ''};
        }
    
        @media (max-width: ${breakPoints.tablet}) {
            ${p[propName]?.tablet?.filter(item => item.indexOf(tag) !== -1).join('') ?? ''};
        }
    
        @media (max-width: ${breakPoints.mobile}) {
            ${p[propName]?.mobile?.filter(item => item.indexOf(tag) !== -1).join('') ?? ''};
        }
    `;
};

export const blockMargin = (p: any) => {
    return css`
        margin-top: ${p.marginTop?.desktop ? `${p.marginTop.desktop}px` : 0};
        margin-bottom: ${p.marginBottom?.desktop ? `${p.marginBottom.desktop}px` : 0};

        @media (max-width: ${breakPoints.wDesktop}) {
            margin-top: ${p.marginTop?.wDesktop ? `${p.marginTop.wDesktop}px` : 0};
            margin-bottom: ${p.marginBottom?.wDesktop ? `${p.marginBottom.wDesktop}px` : 0};
        }
        
        @media (max-width: ${breakPoints.sDesktop}) {
            margin-top: ${p.marginTop?.sDesktop ? `${p.marginTop.sDesktop}px` : 0};
            margin-bottom: ${p.marginBottom?.sDesktop ? `${p.marginBottom.sDesktop}px` : 0};
        }
    
        @media (max-width: ${breakPoints.tablet}) {
            margin-top: ${p.marginTop?.tablet ? `${p.marginTop.tablet}px` : 0};
            margin-bottom: ${p.marginBottom?.tablet ? `${p.marginBottom.tablet}px` : 0};
        }
    
        @media (max-width: ${breakPoints.mobile}) {
            margin-top: ${p.marginTop?.mobile ? `${p.marginTop.mobile}px` : 0};
            margin-bottom: ${p.marginBottom?.mobile ? `${p.marginBottom.mobile}px` : 0};
        }
    `;
};

export const blockWidth = (p: any) => {
    return css`
        flex: 0 1 ${p.blockWidth?.desktop ? `${p.blockWidth.desktop}` : '50%'};

        @media (max-width: ${breakPoints.wDesktop}) {
          flex: 0 1 ${p.blockWidth?.desktop ? `${p.blockWidth.desktop}` : '50%'};
        }
        
        @media (max-width: ${breakPoints.sDesktop}) {
          flex: 0 1 ${p.blockWidth?.desktop ? `${p.blockWidth.desktop}` : '50%'};
        }
    
        @media (max-width: ${breakPoints.tablet}) {
          flex: 0 1 ${p.blockWidth?.desktop ? `${p.blockWidth.desktop}` : '50%'};
        }
    
        @media (max-width: ${breakPoints.mobile}) {
          flex: 0 1 auto;
        }
    `;
};

export const ImagePlaceholder = styled.div<{ width: number; height: number, isAdmin: boolean }>`
    width: ${p => p.width + 'px'};
    height: ${p => p.height + 'px'};
    display: ${p => p.isAdmin ? 'flex' : 'none'};
    background: cornflowerblue;
    overflow: hidden;
    
    &:after {
        width: 100%;
        content: "Image ${p => `${p.width} x ${p.height}`}";
        font: ${fonts.XXL};
        color: #fff;
        text-align: center;
        line-height: ${p => p.height + 'px'};
    }
`;

export const GlobalWrapper = styled.div<{styles: any, background: string}>`
    background-color: ${p => p.background !== '' ? p.background : '#fff' };
    ${p => p.styles}
    
    .nav-tabs .nav-link{
        margin-bottom: 0;
    }
    .nav-tabs .nav-item.show .nav-link,
     .nav-tabs .nav-link.active{
        border: none;
        color: #E24725;
        border-bottom: 2px solid #E24725;
    }
    .nav-tabs a {
        color: #000;
    }

    min-height: 100vh;
`;

export const DateRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: .5rem 0;
`;

export const DateItem = styled.div`
    display: flex;
    color: var(--gray);
    font-size: 80%;
    
    & svg{
        margin-right: 5px;
    }
`;

